// imageCompressService.js
// [Added by E2logy]: Downscales survey photos on the device before they are
// queued/uploaded. A raw tablet camera photo is often 3-8 MB, which is the main
// reason an upload can take 50-60 seconds on a weak store connection. The
// backend already resizes to 1600px wide with sharp, so doing the same on the
// client does not lose any quality that the server would have kept.

const DEFAULT_MAX_WIDTH = 1600;
const DEFAULT_QUALITY = 0.8;
// Below this size compressing is not worth the CPU time on an older tablet.
const MIN_SIZE_TO_COMPRESS = 400 * 1024;

const isCompressibleImage = file =>
  !!file &&
  typeof file === "object" &&
  typeof file.type === "string" &&
  ["image/jpeg", "image/png"].includes(file.type);

const loadBitmap = async file => {
  // createImageBitmap applies the EXIF orientation for us where supported,
  // which keeps photos taken in portrait the right way up.
  if (typeof window.createImageBitmap === "function") {
    try {
      return await window.createImageBitmap(file, {
        imageOrientation: "from-image"
      });
    } catch (error) {
      // Older Safari does not accept the options object, retry without it.
      try {
        return await window.createImageBitmap(file);
      } catch (innerError) {
        // fall through to the <img> based path
      }
    }
  }
  return await new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = error => {
      URL.revokeObjectURL(objectUrl);
      reject(error);
    };
    image.src = objectUrl;
  });
};

const canvasToBlob = (canvas, quality) =>
  new Promise((resolve, reject) => {
    if (typeof canvas.toBlob === "function") {
      canvas.toBlob(
        blob => (blob ? resolve(blob) : reject(new Error("toBlob failed"))),
        "image/jpeg",
        quality
      );
      return;
    }
    try {
      const dataUrl = canvas.toDataURL("image/jpeg", quality);
      const parts = dataUrl.split(",");
      const binary = atob(parts[1]);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      resolve(new Blob([bytes], { type: "image/jpeg" }));
    } catch (error) {
      reject(error);
    }
  });

const buildFileName = originalName => {
  const base =
    originalName && typeof originalName === "string"
      ? originalName.replace(/\.[^.]+$/, "")
      : "photo";
  return `${base || "photo"}.jpg`;
};

const ImageCompressService = {
  /**
   * Returns a downscaled JPEG File for the given image File/Blob.
   * Never throws: if anything goes wrong the original file is returned so the
   * existing upload flow keeps working exactly as before.
   */
  async compress(file, options = {}) {
    const maxWidth = options.maxWidth || DEFAULT_MAX_WIDTH;
    const quality = options.quality || DEFAULT_QUALITY;

    if (!isCompressibleImage(file)) return file;
    if (file.size && file.size < MIN_SIZE_TO_COMPRESS) return file;

    try {
      const source = await loadBitmap(file);
      const sourceWidth = source.width;
      const sourceHeight = source.height;
      if (!sourceWidth || !sourceHeight) return file;

      const scale = Math.min(1, maxWidth / sourceWidth);
      const targetWidth = Math.round(sourceWidth * scale);
      const targetHeight = Math.round(sourceHeight * scale);

      const canvas = document.createElement("canvas");
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const context = canvas.getContext("2d");
      if (!context) return file;
      context.drawImage(source, 0, 0, targetWidth, targetHeight);
      if (typeof source.close === "function") source.close();

      const blob = await canvasToBlob(canvas, quality);
      // Keep the original when compression did not actually help.
      if (!blob || (file.size && blob.size >= file.size)) return file;

      const compressed = new File([blob], buildFileName(file.name), {
        type: "image/jpeg",
        lastModified: Date.now()
      });
      return compressed;
    } catch (error) {
      console.log("compress image error - ", error);
      return file;
    }
  },

  async compressList(files, options = {}) {
    const list = Array.isArray(files) ? files : Array.from(files || []);
    const result = [];
    for (const file of list) {
      result.push(await ImageCompressService.compress(file, options));
    }
    return result;
  }
};

export default ImageCompressService;
